"use client";

import { useRef, type ReactNode } from "react";
import { gsap, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

type DarkPanelProps = {
  children: ReactNode;
  id?: string;
  className?: string;
  panelClassName?: string;
  contentClassName?: string;
  /** Horizontal positions (in %) of faint vertical light threads. */
  threads?: number[];
  /**
   * Scroll-linked entrance: the panel opens from a narrower, rounder capsule to full
   * width while its light blooms — a soft white → black transition instead of a hard cut.
   */
  enter?: boolean;
  /** Scroll-linked exit: recedes (scale .96) and dims its content as it leaves. */
  exit?: boolean;
  ariaLabelledby?: string;
};

export function DarkPanel({
  children,
  id,
  className,
  panelClassName,
  contentClassName,
  threads = [],
  enter = true,
  exit = true,
  ariaLabelledby,
}: DarkPanelProps) {
  const wrap = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = wrap.current;
      if (!el || reduceMotion()) return;
      const panel = el.querySelector<HTMLElement>("[data-dark-panel]");
      const glow = el.querySelector<HTMLElement>("[data-panel-glow]");
      const content = el.querySelector<HTMLElement>("[data-panel-content]");
      if (!panel) return;

      const mm = gsap.matchMedia();
      mm.add({ desktop: "(min-width: 768px)", mobile: "(max-width: 767px)" }, (ctx) => {
        const inset = ctx.conditions?.desktop ? 5 : 2.5;

        if (enter) {
          gsap.set(panel, { clipPath: "inset(0% var(--clip-x) 0% var(--clip-x) round var(--clip-r))" });
          gsap
            .timeline({
              scrollTrigger: { trigger: el, start: "top bottom", end: "top 30%", scrub: 0.9 },
            })
            .fromTo(
              panel,
              { "--clip-x": `${inset}%`, "--clip-r": "56px" },
              { "--clip-x": "0%", "--clip-r": "28px", ease: "drift" },
              0,
            )
            .fromTo(
              glow,
              { opacity: 0, scale: 0.8 },
              { opacity: 1, scale: 1, ease: "drift" },
              0.15,
            );
        }

        if (exit) {
          gsap
            .timeline({
              scrollTrigger: { trigger: el, start: "bottom 70%", end: "bottom top", scrub: true },
            })
            .to(panel, { scale: 0.96, ease: "none" }, 0)
            .to(content, { opacity: 0.35, y: -30, ease: "none" }, 0);
        }
      });
    },
    { scope: wrap },
  );

  return (
    <section
      ref={wrap}
      id={id}
      aria-labelledby={ariaLabelledby}
      data-nav-theme="dark"
      className={cn("frame", className)}
    >
      <div data-dark-panel="" className={cn("panel-dark", panelClassName)}>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div data-panel-glow-wrap="" className="absolute inset-0">
            <div data-panel-glow="" className="panel-glow" />
            <div className="panel-glow-soft" />
          </div>
          <div data-panel-grid="" className="panel-grid" />
          {threads.map((x, i) => (
            <span
              key={x}
              className="panel-thread animate-beam"
              style={{ left: `${x}%`, animationDelay: `${i * -2.3}s` }}
            />
          ))}
        </div>
        <div data-panel-content="" className={cn("relative z-10", contentClassName)}>
          {children}
        </div>
      </div>
    </section>
  );
}
